import "./DealStagePill.css";

import { useEffect, useMemo, useRef, useState } from "react";

import { BeforePhaseRingIcon, CompletedPhaseTickIcon, DuringPhaseRingIcon } from "../../../../assets";

type DealStageItem = {
  key: string;
  label: string;
  status?: string | null;
};

interface DealStagePillProps {
  label: string;
  stages: DealStageItem[];
  selectedStageLabel?: string | null;
  onStageClick?: (stage: DealStageItem) => void;
}

const getStatusType = (status?: string | null): "completed" | "in-progress" | "todo" => {
  if (!status) return "todo";
  const normalized = status.toLowerCase().replace(/[^a-z]/g, "");
  if (normalized.includes("complete") || normalized.includes("done")) return "completed";
  if (normalized.includes("progress")) return "in-progress";
  if (normalized.includes("todo")) return "todo";
  return "todo";
};

// TODO-JWI: Lets match this to design + need redonly when in deal listing, side note - invalid_phase in steps is an issue for project?

export const DealStagePill = ({ label, stages, selectedStageLabel, onStageClick }: DealStagePillProps): JSX.Element => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedStageKey = useMemo(
    () => selectedStageLabel ?? stages.find((stage) => stage.key === label || stage.label === label)?.key,
    [label, selectedStageLabel, stages]
  );
  const currentStage = useMemo(() => {
    return (
      stages.find((stage) => stage.key === selectedStageKey) ??
      stages.find((stage) => stage.key === label || stage.label === label)
    );
  }, [label, selectedStageKey, stages]);

  const currentStatusType = useMemo(() => getStatusType(currentStage?.status), [currentStage?.status]);

  const stagePosition = useMemo(() => {
    const idx = stages.findIndex(
      (stage) => stage.key === selectedStageKey || stage.key === label || stage.label === label
    );
    return idx >= 0 ? idx + 1 : 1;
  }, [label, selectedStageKey, stages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setIsTooltipVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} className="DealStagePill" onMouseEnter={() => setIsTooltipVisible(true)}>
      <span className="DealStagePillStatusIcon" aria-hidden>
        {currentStatusType === "completed" && <CompletedPhaseTickIcon className="DealStageStatusIcon Large" />}
        {currentStatusType === "in-progress" && <DuringPhaseRingIcon className="DealStageStatusIcon Large" />}
        {currentStatusType === "todo" && <BeforePhaseRingIcon className="DealStageStatusIcon Large" />}
      </span>
      <p className="body2 DealStagePillLabel">{label}</p>
      <span aria-hidden />
      {isTooltipVisible && stages.length > 0 ? (
        <div className="DealStageTooltip" role="tooltip">
          <div className="DealStageTooltipArrow" />
          <div className="DealStageTooltipContent">
            <p className="DealStageTooltipHeader">
              Stage {stagePosition} of {stages.length}
            </p>
            <div className="DealStageTooltipList">
              {stages.map((stage) => {
                const statusType = getStatusType(stage.status);
                const isSelected = stage.key === selectedStageKey;
                return (
                  <button
                    key={stage.key}
                    type="button"
                    className={`DealStageTooltipRow ${statusType} ${isSelected ? "Selected" : ""} ${
                      onStageClick ? "Clickable" : ""
                    }`}
                    onClick={() => onStageClick?.(stage)}
                  >
                    {statusType === "completed" && <CompletedPhaseTickIcon className="DealStageStatusIcon" />}
                    {statusType === "in-progress" && <DuringPhaseRingIcon className="DealStageStatusIcon" />}
                    {statusType === "todo" && <BeforePhaseRingIcon className="DealStageStatusIcon" />}
                    <span className="DealStageTooltipLabel">{stage.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
